import { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import { db } from '@/lib/db';
import { useAuth } from '@/hooks/use-auth';
import { useTenant } from '@/contexts/tenant-context';

interface SyncCounts {
  pendingSync: number;
  pendingMedia: number;
  failedSync: number;
  failedMedia: number;
  totalPending: number;
  totalFailed: number;
}

/**
 * Hook that provides reactive sync queue counts using Dexie's liveQuery.
 *
 * This automatically updates whenever the IndexedDB tables change,
 * providing real-time feedback on sync status in the UI.
 */
export function useSyncStatus(): SyncCounts {
  const { user } = useAuth();
  const { activeTenantId } = useTenant();
  const [counts, setCounts] = useState<SyncCounts>({
    pendingSync: 0,
    pendingMedia: 0,
    failedSync: 0,
    failedMedia: 0,
    totalPending: 0,
    totalFailed: 0,
  });
  const scopeKey = user?.uid && activeTenantId
    ? `${user.uid}:${activeTenantId}`
    : 'anonymous';

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const [pendingSync, pendingMedia, failedSync, failedMedia] = await Promise.all([
        db.syncQueue.where('status').anyOf('pending', 'syncing').count(),
        db.mediaQueue.where('status').anyOf('pending', 'syncing').count(),
        db.syncQueue.where('status').equals('failed').count(),
        db.mediaQueue.where('status').equals('failed').count(),
      ]);

      return {
        pendingSync,
        pendingMedia,
        failedSync,
        failedMedia,
        totalPending: pendingSync + pendingMedia,
        totalFailed: failedSync + failedMedia,
      };
    }).subscribe({
      next: (result) => setCounts(result),
      error: (err) => console.error('[useSyncStatus] LiveQuery error:', err),
    });

    return () => subscription.unsubscribe();
  }, [scopeKey]);

  return counts;
}
